/**
 * Timesheets — redesigned (EM handoff): manual log card + entries grouped by
 * date (left), range summary + billable split + utilization (right).
 * The live start/stop timer lives in the Workspace header (TimerWidget).
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "../../utils/toast";
import {
  Plus,
  Trash2,
  Download,
  Upload,
  Copy,
  X,
  Pencil,
  CalendarOff,
  Play,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { confirmDialog } from "../../components/em/confirm";
import { wsApi } from "./wsApi";
import { hasPermission } from "../../utils/permissionUtils";
import ImportTimesheetModal from "./ImportTimesheetModal";
import useNonWorkingDays, { nonWorkingLabel } from "./useNonWorkingDays";
import NonWorkingDayWarning from "./NonWorkingDayWarning";
import { T, Avatar, Label, Input, Select, Pagination } from "../../components/em/ui";
import { Panel, Skeleton, RowMenu, EmployeeMultiSelect, FilterSelect, byName } from "./wsUi";
import {
  fmtDuration,
  fmtDate,
  fmtTime,
  toLocalInputValue,
  localInputToIso,
  localDateStr,
  utcNaiveToDate,
  MAX_ENTRY_HOURS,
  durationInputError,
  wrapSpinValue,
} from "./wsConstants";
import {
  downloadTimesheetCSV,
  downloadTimesheetExcel,
  downloadTimesheetPDF,
  timesheetGroupKey,
} from "./lib/timesheetExport";

// Red border + tint for an hrs / min field that fails validation.
const invalidInput = { borderColor: "#ef4444", background: "#fef2f2" };

// Local YYYY-MM-DD (shared helper) — the filter/date fields are calendar days.
const isoDate = (d) => localDateStr(d);
const startOfMonth = () => {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};
const today = () => isoDate(new Date());

// Rows (a grouped task counts as one) per page of the entries list.
const ENTRIES_PER_PAGE = 50;

// Quick ranges for the Filters panel. Each returns [from, to] as local
// YYYY-MM-DD. Both end at today so the pair reads as one scale — "Last week"
// is the trailing 7 days (today included) rather than the previous Mon–Sun
// calendar week, which would be the only preset not ending now.
const QUICK_RANGES = [
  {
    key: "week",
    label: "Last week",
    title: "Last 7 days",
    range: () => {
      const d = new Date();
      return [isoDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 6)), isoDate(d)];
    },
  },
  {
    key: "month",
    label: "Monthly",
    title: "This month so far",
    range: () => [startOfMonth(), today()],
  },
];

// Compact field metrics for the filter column only — six stacked fields at the
// design system's default label gap and control height overran the card. The
// shared <Label>/<Input>/<Select> keep their normal size everywhere else.
const FIELD_SM = { padding: "6px 10px", font: `400 12px/1.4 ${T.font}` };
const SELECT_SM = { padding: "6px 28px 6px 10px", font: `500 12px/1.4 ${T.font}` };

// Project pickers are FilterSelect, as on the Workspace overview: a native
// <select> with many projects flipped its list over the field. These styles
// size its closed field like the <Select>s beside it (SELECT_SM in the filter
// bar, the shared full-size one in forms). height:auto undoes the global
// 40px `button` box.
const FILTER_SELECT_SM = { ...SELECT_SM, height: "auto", color: T.fg1 };
const FILTER_SELECT_FIELD = {
  height: "auto",
  padding: "9px 30px 9px 12px",
  borderRadius: T.rMd,
  font: `500 13px/1.4 ${T.font}`,
  color: T.fg1,
};
const projectOptionsOf = (list) => list.map((p) => ({ value: String(p.id), label: p.name }));

const FilterField = ({ label, children }) => (
  <div>
    <span
      style={{
        display: "block",
        font: `500 11px/1.3 ${T.font}`,
        color: T.fg3,
        marginBottom: 3,
      }}
    >
      {label}
    </span>
    {children}
  </div>
);

export default function Timesheets() {
  // Company-wide timesheet authority — the key the /time-entries endpoints
  // check before widening past your team.
  const manager = hasPermission("manage_all_timesheets");
  // Export runs entirely in the browser over the rows already fetched, so this
  // hides the control — it is NOT a data boundary. What a person can export is
  // bounded by what /time-entries returns to them, which the server scopes.
  const canExport = hasPermission("export_timesheets");

  // The team controls used to be gated on isManager() — a pure role check for
  // Admin/HR. A custom role such as "Manager" arrives as `member` in the JWT,
  // so the controls stayed hidden no matter what Roles & Access granted, even
  // though the server would have allowed the request. Gate on the permission.
  const canViewTeam = manager || hasPermission("view_team_timesheets");
  const canManageEntries = manager || hasPermission("manage_timesheet_entries");
  const canImport = hasPermission("import_timesheets");
  const myUserId = (() => {
    try {
      return Number(JSON.parse(localStorage.getItem("userdata") || "{}")?.id) || null;
    } catch {
      return null;
    }
  })();

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  // Employee filter — ids picked in EmployeeMultiSelect; empty = everyone.
  // Defaults to the logged-in user so the page opens on your own entries.
  const [userIds, setUserIds] = useState(() => (myUserId ? [String(myUserId)] : []));
  const [filterProject, setFilterProject] = useState("");
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState([]);
  const [showDownload, setShowDownload] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  // Entries-list page (1-based). Only the rendered rows page — the totals,
  // "This range" panel and exports always cover the whole range.
  const [page, setPage] = useState(1);
  const listRef = useRef(null);
  // Same-task groups (see groupEntries) whose session chips are expanded.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // 'duration' = hrs/min + a calendar day; 'range' = explicit start/end
  // timestamps (the server derives the duration and the local day from them).
  // Kept outside `form` so logging an entry doesn't snap the mode back.
  const [logMode, setLogMode] = useState("duration");
  const [form, setForm] = useState({
    project_id: "",
    task_id: "",
    description: "",
    hours: "",
    minutes: "",
    billable: true,
    entry_date: today(),
    started_at: toLocalInputValue(),
    ended_at: toLocalInputValue(),
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Tasks of the picked project that you work on, for the optional Task
  // select. A task belongs to one project, so switching project clears it.
  const logTasks = useProjectTasks(form.project_id, myUserId);
  const setLogProject = (id) => setForm((f) => ({ ...f, project_id: id, task_id: "" }));
  // Week-offs + holidays, so logging onto a Sunday or a public holiday warns
  // first. Never blocks — see NonWorkingDayWarning.
  const { checkDay } = useNonWorkingDays();
  // { body, warning } parked while the user answers the warning dialog.
  const [pendingLog, setPendingLog] = useState(null);

  // `silent` skips the loading skeleton — for the background refresh below.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    // The server rejects a user_id outside the caller's own team, so sending it
    // for a team-scoped viewer is safe — it narrows within their team.
    if (canViewTeam && userIds.length) p.set("user_ids", userIds.join(","));
    if (filterProject) p.set("project_id", filterProject);
    if (teamId) p.set("team_id", teamId);
    wsApi
      .listTimeEntries(`?${p.toString()}`)
      .then(setEntries)
      .catch(() => toast.error("Could not load timesheets"))
      .finally(() => { if (!silent) setLoading(false); });
  }, [from, to, userIds, filterProject, teamId, canViewTeam]);

  useEffect(() => {
    load();
  }, [load]);

  // A new range or filter is a new list — start it from the top.
  useEffect(() => {
    setPage(1);
  }, [from, to, userIds, filterProject, teamId]);

  // Stopping the header's live timer writes an entry that belongs in this list.
  useEffect(() => {
    const handler = () => load(true);
    window.addEventListener("workspaceDataChanged", handler);
    return () => window.removeEventListener("workspaceDataChanged", handler);
  }, [load]);

  // The live timer, so a row can show that its task is the one running. Kept
  // in step with the header's TimerWidget through the same event.
  const [activeTimer, setActiveTimer] = useState(null);
  const [resumingId, setResumingId] = useState(null);
  useEffect(() => {
    const refresh = () => wsApi.getTimer().then(setActiveTimer).catch(() => {});
    refresh();
    window.addEventListener("workspaceTimerChanged", refresh);
    window.addEventListener("workspaceDataChanged", refresh);
    return () => {
      window.removeEventListener("workspaceTimerChanged", refresh);
      window.removeEventListener("workspaceDataChanged", refresh);
    };
  }, []);
  // Same task, project and description as the running timer → it's this row's
  // work that is being tracked right now.
  const isRunning = (e) =>
    !!activeTimer &&
    Number(activeTimer.task_id || 0) === Number(e.task_id || 0) &&
    Number(activeTimer.project_id || 0) === Number(e.project_id || 0) &&
    String(activeTimer.description || "") === String(e.description || "");

  // Resume — start the live timer on this entry's task (project, task,
  // description and billable flag carried over). The server stops any timer
  // already running first and logs its time, so nothing is lost.
  const resume = async (e) => {
    setResumingId(e.id);
    try {
      const t = await wsApi.startTimer({
        task_id: e.task_id || null,
        project_id: e.project_id || null,
        description: e.description || e.task_title || null,
        billable: e.billable ? 1 : 0,
      });
      setActiveTimer(t);
      window.dispatchEvent(new Event("workspaceTimerChanged"));
      if (t?.stopped) {
        toast.success(`Previous timer stopped (${fmtDuration(t.stopped.duration_minutes)} logged) — timer resumed`);
        load(true);
      } else {
        toast.success("Timer resumed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not resume timer");
    } finally {
      setResumingId(null);
    }
  };
  useEffect(() => {
    wsApi
      .listProjects()
      .then((p) => setProjects(byName(p)))
      .catch(() => { });
    wsApi
      .listTeams()
      .then(setTeams)
      .catch(() => { });
    if (canViewTeam)
      wsApi
        .teamUsers()
        .then(setUsers)
        .catch(() => { });
  }, [canViewTeam]);

  const doExport = (kind) => {
    setShowDownload(false);
    if (!entries.length) return toast.error("No entries to export");
    if (kind === "csv") downloadTimesheetCSV(entries);
    else if (kind === "excel") downloadTimesheetExcel(entries);
    else downloadTimesheetPDF(entries, { from, to });
  };

  // Live preview of the range duration (minutes) — null if the range is invalid.
  const logRangeMins = (() => {
    if (logMode !== "range" || !form.started_at || !form.ended_at) return null;
    const ms =
      new Date(form.ended_at).getTime() - new Date(form.started_at).getTime();
    return ms > 0 ? Math.round(ms / 60000) : null;
  })();
  // Live: hrs/min over 24 hours etc., or a Start & end range over 24 hours.
  const logInputError =
    logMode === "range"
      ? logRangeMins != null && logRangeMins > MAX_ENTRY_HOURS * 60
        ? `A single entry can't be more than ${MAX_ENTRY_HOURS} hours.`
        : null
      : durationInputError(form.hours, form.minutes);

  // The calendar day this entry will land on, whichever mode is active — in
  // range mode the server derives it from the start instant, so the warning has
  // to read it the same way.
  const logEntryDate =
    logMode === "range"
      ? form.started_at
        ? localDateStr(new Date(form.started_at))
        : ""
      : form.entry_date;
  // Non-null when that day is a week-off or holiday. Drives the inline hint
  // below the fields as well as the confirm dialog.
  const logDayWarning = checkDay(logEntryDate);

  // Validation only — returns the request body, or null after toasting why not.
  const buildLogBody = () => {
    if (!form.project_id) {
      toast.error("Select a project");
      return null;
    }
    if (!form.description.trim()) {
      toast.error("Enter a description");
      return null;
    }
    let body = {
      project_id: form.project_id || null,
      task_id: form.task_id || null,
      description: form.description,
      billable: form.billable ? 1 : 0,
    };
    if (logMode === "range") {
      if (!form.started_at || !form.ended_at) {
        toast.error("Pick a start and end time");
        return null;
      }
      if (logRangeMins == null) {
        toast.error("End time must be after start time");
        return null;
      }
      if (logInputError) {
        toast.error(logInputError);
        return null;
      }
      // Send true instants (ISO) + the local calendar day, so the server never
      // has to guess a zone for the wall-clock times the user typed.
      return {
        ...body,
        started_at: localInputToIso(form.started_at),
        ended_at: localInputToIso(form.ended_at),
        entry_date: localDateStr(new Date(form.started_at)),
      };
    }
    const logDurationError = durationInputError(form.hours, form.minutes);
    if (logDurationError) {
      toast.error(logDurationError);
      return null;
    }
    const mins =
      (parseInt(form.hours) || 0) * 60 + (parseInt(form.minutes) || 0);
    if (mins <= 0) {
      toast.error("Enter a duration");
      return null;
    }
    return { ...body, duration_minutes: mins, entry_date: form.entry_date };
  };

  const submitLog = async (body) => {
    setSaving(true);
    try {
      await wsApi.addTimeEntry(body);
      setForm({
        project_id: "",
        task_id: "",
        description: "",
        hours: "",
        minutes: "",
        billable: true,
        entry_date: today(),
        started_at: toLocalInputValue(),
        ended_at: toLocalInputValue(),
      });
      setPendingLog(null);
      toast.success("Time logged");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not log time");
    } finally {
      setSaving(false);
    }
  };

  // Validate, then warn on a non-working day before saving. The warning is a
  // detour, not a wall: confirming sends the identical body.
  const log = () => {
    const body = buildLogBody();
    if (!body) return;
    const warning = checkDay(body.entry_date);
    if (warning) return setPendingLog({ body, warning });
    submitLog(body);
  };

  // The row is dropped only after the server confirms the delete — removing it
  // optimistically is how a failed delete used to look like a successful one
  // until the next refresh.
  const del = async (id) => {
    try {
      await wsApi.deleteTimeEntry(id);
      setEntries((e) => e.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not delete");
    }
  };


  // Duplicate — one new entry carrying every field of the original: project,
  // task, description, billable flag, day and, when the original had them, the
  // same start/end instants. The copy is attributed to the ORIGINAL owner
  // (user_id), so a manager duplicating someone else's row doesn't steal it;
  // the server ignores that field for non-managers. Status starts at 'pending'
  // like any freshly logged entry.
  const duplicate = async (e) => {
    setDuplicatingId(e.id);
    const body = {
      task_id: e.task_id || null,
      project_id: e.project_id || null,
      description: e.description || "",
      billable: e.billable ? 1 : 0,
      entry_date: String(e.entry_date || "").slice(0, 10),
      user_id: e.user_id,
    };
    const start = utcNaiveToDate(e.started_at);
    const end = utcNaiveToDate(e.ended_at);
    if (start && end) {
      body.started_at = start.toISOString();
      body.ended_at = end.toISOString();
    } else {
      body.duration_minutes = e.duration_minutes;
    }
    try {
      await wsApi.addTimeEntry(body);
      toast.success("Entry duplicated");
      load(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not duplicate entry");
    } finally {
      setDuplicatingId(null);
    }
  };

  // Which quick range (if any) the current From/To pair matches — the presets
  // are stateless, so the highlight is derived rather than remembered. Typing a
  // date by hand that happens to equal a preset lights it up, which is right.
  const activeQuick = QUICK_RANGES.find((r) => {
    const [f, t] = r.range();
    return f === from && t === to;
  })?.key;

  const totalMins = entries.reduce((s, e) => s + e.duration_minutes, 0);
  const billMins = entries
    .filter((e) => e.billable)
    .reduce((s, e) => s + e.duration_minutes, 0);

  const byDate = {};
  entries.forEach((e) => {
    (byDate[e.entry_date] = byDate[e.entry_date] || []).push(e);
  });
  const dates = Object.keys(byDate).sort((a, b) => (a < b ? 1 : -1));

  // Within a day, entries for the same piece of work — same owner, project,
  // task, description and billable flag — collapse into one row with a chip
  // per session (a timer resumed three times is one task, not three rows).
  // Order follows each group's first entry, so the list keeps its sort.
  // The key is shared with the downloads, so the files group the same way.
  const groupKeyOf = timesheetGroupKey;
  const groupEntries = (list) => {
    const map = new Map();
    list.forEach((e) => {
      const k = groupKeyOf(e);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return [...map.entries()].map(([key, items]) => ({ key, items }));
  };
  // Every rendered row in display order (newest day first), one per group, so
  // a grouped task's sessions never split across pages.
  const rows = dates.flatMap((d) => groupEntries(byDate[d]).map((g) => ({ date: d, g })));
  const taskCount = rows.length;

  // Pagination over those rows. Clamped rather than reset, so deleting the last
  // row of the last page lands on the new last page.
  const totalPages = Math.max(1, Math.ceil(rows.length / ENTRIES_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * ENTRIES_PER_PAGE, safePage * ENTRIES_PER_PAGE);
  // Re-grouped by day for the headers; a day cut by a page boundary repeats its
  // header on the next page, still showing the whole day's total.
  const pageDays = [];
  pageRows.forEach(({ date, g }) => {
    const last = pageDays[pageDays.length - 1];
    if (last && last.date === date) last.groups.push(g);
    else pageDays.push({ date, groups: [g] });
  });
  const goToPage = (p) => {
    setPage(p);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const canEditEntry = (e) => canManageEntries || !myUserId || Number(e.user_id) === myUserId;
  // Own entries are always editable. Someone else's need
  // `manage_timesheet_entries` — the server enforces the same rule, so showing
  // the action otherwise would just produce a 403.
  const entryMenuItems = (e) => [
    ...(canEditEntry(e)
      ? [{ key: "edit", label: "Edit entry", icon: Pencil, onClick: () => setEditEntry(e) }]
      : []),
    {
      key: "duplicate",
      label: "Duplicate",
      icon: Copy,
      disabled: duplicatingId === e.id,
      onClick: () => duplicate(e),
    },
    ...(canEditEntry(e)
      ? [{ key: "delete", label: "Delete", icon: Trash2, danger: true, onClick: () => del(e.id) }]
      : []),
  ];

  // Delete every session of a grouped task — one confirm, then one delete per
  // entry (there is no bulk endpoint). Only rows the server confirms go away.
  const delGroup = async (items) => {
    const ok = await confirmDialog({
      message: `Delete all ${items.length} sessions of this task? This can't be undone.`,
      confirmLabel: "Delete all",
      danger: true,
    });
    if (!ok) return;
    const results = await Promise.allSettled(items.map((e) => wsApi.deleteTimeEntry(e.id)));
    const gone = new Set(items.filter((_, i) => results[i].status === "fulfilled").map((e) => e.id));
    setEntries((list) => list.filter((x) => !gone.has(x.id)));
    if (gone.size < items.length) toast.error(`${items.length - gone.size} session(s) could not be deleted`);
  };

  // Time span of a session, or of a whole group (earliest start – latest end)
  // in the viewer's zone. Null when nothing has start/end times (manual entry).
  const spanOf = (items) => {
    const timed = items.filter((e) => e.started_at && e.ended_at);
    if (!timed.length) return null;
    const first = timed.reduce((a, b) => (utcNaiveToDate(b.started_at) < utcNaiveToDate(a.started_at) ? b : a));
    const last = timed.reduce((a, b) => (utcNaiveToDate(b.ended_at) > utcNaiveToDate(a.ended_at) ? b : a));
    return `${fmtTime(first.started_at)} – ${fmtTime(last.ended_at)}`;
  };

  const pill = (bg, color, text, title) => (
    <span
      title={title || ""}
      style={{
        padding: "2px 8px",
        borderRadius: 9999,
        background: bg,
        color,
        font: `600 10px ${T.font}`,
        maxWidth: 120,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flexShrink: 0,
      }}
    >
      {text}
    </span>
  );
  const durationText = (mins) => (
    <span
      style={{
        font: `700 12px ${T.font}`,
        color: T.fg1,
        fontVariantNumeric: "tabular-nums",
        width: 50,
        textAlign: "right",
        flexShrink: 0,
      }}
    >
      {fmtDuration(mins)}
    </span>
  );
  // Actions collapsed behind a ⋮ menu. RowMenu keeps a single fixed-width
  // affordance so every row's columns line up.
  const menuSlot = (label, items) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: 28, flexShrink: 0 }}>
      <RowMenu label={label} items={items} />
    </div>
  );

  // Resume — after the actions. Only on your own entries: the live timer is
  // always yours, so resuming someone else's row would log their task as your
  // time. Always a fixed-width slot so rows keep their columns aligned.
  const resumeSlot = (e) => {
    const mine = !myUserId || Number(e.user_id) === myUserId;
    const running = mine && isRunning(e);
    const busy = resumingId === e.id;
    return (
      <div style={{ width: 30, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
        {mine && (
          <button
            type="button"
            onClick={() => !running && !busy && resume(e)}
            disabled={running || busy}
            title={running ? "This task's timer is running" : "Resume — start the timer on this task"}
            aria-label={running ? "Timer running" : "Resume timer"}
            // Pinned to an exact 28×28 square: the app-wide button rules
            // (padding, line-height, min-height) and `button svg { width:16px;
            // top:2px; margin-right:6px }` otherwise stretch it taller than
            // wide and push the icon off-centre.
            style={{
              boxSizing: "border-box",
              flex: "0 0 28px",
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              maxWidth: 28,
              maxHeight: 28,
              margin: 0,
              padding: 0,
              lineHeight: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
              border: `1px solid ${running ? T.orange : T.border}`,
              background: running ? T.orange50 : "transparent",
              color: T.orange,
              cursor: running || busy ? "default" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            {running ? (
              <span style={{ display: "block", width: 8, height: 8, borderRadius: "50%", background: T.orange, animation: "wsPulse 1.2s infinite" }} />
            ) : (
              <Play
                size={12}
                fill="currentColor"
                // Nudged right 1px: a play triangle's visual centre sits left
                // of its box's centre.
                style={{ display: "block", width: 12, height: 12, margin: "0 0 0 1px", position: "static", top: "auto" }}
              />
            )}
          </button>
        )}
      </div>
    );
  };

  const titleText = (e) => (
    <div
      style={{
        font: `500 13px ${T.font}`,
        color: T.fg1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {e.description || e.task_title || "—"}
    </div>
  );
  // "Project › Task" — the task is named here because the title line shows the
  // description whenever there is one.
  const projectTaskLabel = (e) =>
    `${e.project_name || "Global"}${e.task_title ? ` › ${e.task_title}` : ""}`;
  const subText = (children) => (
    <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 1 }}>{children}</div>
  );
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 18px",
  };
  // Owner + billable pills, shared by single rows and grouped rows.
  // (PARKED with the Jira integration: a synced worklog whose author matched no
  // account was stored unassigned; while Jira is off the owner is always set.)
  const pills = (e) => (
    <>
      {pill(T.bgMuted, T.fg5, e.username || "—", e.username)}
      {pill(e.billable ? T.success50 : T.bgMuted, e.billable ? T.success600 : T.fg5, e.billable ? "Billable" : "Internal")}
    </>
  );

  // One entry on its own — the original row layout.
  const renderSingle = (e) => (
    <div key={e.id} style={{ ...rowStyle, borderBottom: `1px solid ${T.divider}` }}>
      <Avatar name={e.username} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {titleText(e)}
        {/* Start–end shown in the viewer's local zone (the stored value is
            UTC — see wsConstants helpers). */}
        {subText(`${projectTaskLabel(e)}${spanOf([e]) ? ` · ${spanOf([e])}` : ""}`)}
      </div>
      {pills(e)}
      {durationText(e.duration_minutes)}
      {menuSlot("Entry actions", entryMenuItems(e))}
      {resumeSlot(e)}
    </div>
  );

  // Several sessions of one task — a count badge in place of the avatar, the
  // combined span and total, and (expanded) a chip per session carrying that
  // session's own actions. Play resumes the latest session.
  const renderGroup = (date, { key, items }) => {
    const gKey = `${date}|${key}`;
    const open = openGroups.has(gKey);
    const head = items[0];
    const total = items.reduce((s, e) => s + e.duration_minutes, 0);
    const span = spanOf(items);
    const allEditable = items.every(canEditEntry);
    return (
      <div key={gKey} style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div style={rowStyle}>
          <button
            type="button"
            onClick={() => toggleGroup(gKey)}
            aria-expanded={open}
            title={open ? "Hide sessions" : "Show sessions"}
            style={{
              boxSizing: "border-box",
              flex: "0 0 26px",
              width: 26,
              height: 26,
              minHeight: 26,
              margin: 0,
              padding: 0,
              lineHeight: "26px",
              border: 0,
              borderRadius: 7,
              cursor: "pointer",
              background: open ? "#3b82f6" : "#e0f2fe",
              color: open ? "#fff" : "#0369a1",
              font: `700 12px ${T.font}`,
              textAlign: "center",
            }}
          >
            {items.length}
          </button>
          <div
            style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
            onClick={() => toggleGroup(gKey)}
          >
            {titleText(head)}
            {subText(
              `${projectTaskLabel(head)}${span ? ` · ${span}` : ""} · ${items.length} sessions`,
            )}
          </div>
          {pills(head)}
          {durationText(total)}
          {menuSlot("Task actions", [
            {
              key: "toggle",
              label: open ? "Hide sessions" : "Show sessions",
              icon: open ? ChevronUp : ChevronDown,
              onClick: () => toggleGroup(gKey),
            },
            ...(allEditable
              ? [{ key: "delete-all", label: `Delete all ${items.length} sessions`, icon: Trash2, danger: true, onClick: () => delGroup(items) }]
              : []),
          ])}
          {resumeSlot(head)}
        </div>
        {open && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              // Lines the chips up under the title: row padding + badge + gap.
              padding: "0 18px 14px 56px",
            }}
          >
            {items.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "4px 4px 4px 12px",
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  background: "var(--em-bg-card)",
                }}
              >
                <span style={{ font: `500 12px ${T.font}`, color: T.fg3, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {spanOf([e]) || "Manual entry"}
                </span>
                <span style={{ font: `700 12px ${T.font}`, color: T.fg1, fontVariantNumeric: "tabular-nums" }}>
                  {fmtDuration(e.duration_minutes)}
                </span>
                {menuSlot("Session actions", entryMenuItems(e))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) 340px",
        gap: 16,
        alignItems: "start",
      }}
    >
      {/* Left: log + entries */}
      <div>
        {/* Manual log card */}
        <div
          style={{
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: 16,
            padding: "18px 20px",
            boxShadow: T.shadowSm,
            marginBottom: 16,
          }}
        >
          {/* Title + Duration | Start & end toggle — mirrors the task Time tab
              and the edit-entry modal below. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <div style={{ font: `700 14px ${T.font}`, color: T.fg1 }}>
              Log time
            </div>
            <div
              style={{
                display: "flex",
                background: T.bgMuted,
                borderRadius: 8,
                padding: 2,
              }}
            >
              {[
                ["duration", "Duration"],
                ["range", "Start & end"],
              ].map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => setLogMode(m)}
                  style={{
                    border: 0,
                    borderRadius: 6,
                    padding: "5px 12px",
                    cursor: "pointer",
                    font: `600 12px ${T.font}`,
                    background: logMode === m ? T.orange : "transparent",
                    color: logMode === m ? "#fff" : T.fg5,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: 2, minWidth: 150 }}>
              <Label>Project</Label>
              <FilterSelect
                value={form.project_id}
                onChange={setLogProject}
                allLabel="Select a project"
                buttonStyle={FILTER_SELECT_FIELD}
                options={projectOptionsOf(projects)}
              />
            </div>
            <div style={{ flex: 2, minWidth: 150 }}>
              <Label>Task</Label>
              <Select
                value={form.task_id}
                disabled={!form.project_id}
                onChange={(e) => setF("task_id", e.target.value)}
              >
                <option value="">
                  {form.project_id ? "No task" : "Select a project first"}
                </option>
                {logTasks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 3, minWidth: 180 }}>
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setF("description", e.target.value)}
                placeholder="What did you work on?"
                required
              />
            </div>
            {logMode === "duration" ? (
              <>
                <div style={{ width: 64 }}>
                  <Label>Hrs</Label>
                  <Input
                    type="number"
                    value={form.hours}
                    onChange={(e) => setF("hours", wrapSpinValue(form.hours, e.target.value, MAX_ENTRY_HOURS))}
                    placeholder="0"
                    aria-invalid={!!logInputError}
                    style={logInputError ? invalidInput : undefined}
                  />
                </div>
                <div style={{ width: 64 }}>
                  <Label>Min</Label>
                  <Input
                    type="number"
                    value={form.minutes}
                    onChange={(e) => setF("minutes", wrapSpinValue(form.minutes, e.target.value, 59))}
                    placeholder="0"
                    aria-invalid={!!logInputError}
                    style={logInputError ? invalidInput : undefined}
                  />
                </div>
                <div style={{ width: 140 }}>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.entry_date}
                    onChange={(e) => setF("entry_date", e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div style={{ flex: 1, minWidth: 185 }}>
                  <Label>Start</Label>
                  <Input
                    type="datetime-local"
                    value={form.started_at}
                    onChange={(e) => setF("started_at", e.target.value)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 185 }}>
                  <Label>End</Label>
                  <Input
                    type="datetime-local"
                    value={form.ended_at}
                    onChange={(e) => setF("ended_at", e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
          {logMode === "range" && (
            <div
              style={{
                font: `500 11.5px ${T.font}`,
                color: logRangeMins == null ? T.danger : T.fg7,
                marginTop: 8,
              }}
            >
              {logRangeMins == null
                ? "End time must be after start time"
                : `Duration: ${fmtDuration(logRangeMins)} · logged on ${fmtDate(form.started_at)}`}
            </div>
          )}
          {/* Over 24 hours (or minutes > 59, negative) — flagged as it's typed. */}
          {logInputError && (
            <div role="alert" style={{ font: `500 11.5px ${T.font}`, color: T.danger, marginTop: 8 }}>
              {logInputError}
            </div>
          )}
          {/* Heads-up before the click, so the dialog on save is never a
              surprise. Advisory only — nothing here disables Log time. */}
          {logDayWarning && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 8,
                font: `500 11.5px ${T.font}`,
                color: T.warn,
              }}
            >
              <CalendarOff size={13} />
              Selected date is {nonWorkingLabel(logDayWarning)}.
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 12,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                font: `500 13px ${T.font}`,
                color: T.fg3,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.billable}
                onChange={(e) => setF("billable", e.target.checked)}
                style={{ accentColor: T.orange }}
              />{" "}
              Billable
            </label>
            <button
              onClick={log}
              disabled={saving}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "9px 16px",
                borderRadius: T.rMd,
                border: 0,
                cursor: "pointer",
                background: T.gradient,
                color: "#fff",
                font: `600 13px ${T.font}`,
                boxShadow: T.shadowBrand,
              }}
            >
              <Plus size={15} /> Log time
            </button>
          </div>
        </div>

        {/* Entries grouped by date */}
        <div
          ref={listRef}
          style={{
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: `1px solid ${T.divider}`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ font: `700 14px ${T.font}`, color: T.fg1, flex: 1 }}>
              Time entries
            </span>
            <span style={{ font: `500 12px ${T.font}`, color: T.fg7, marginRight: 12 }}>
              {entries.length} entries
              {taskCount < entries.length ? ` · ${taskCount} tasks` : ""}
            </span>
            {/* No Jira control here on purpose: Jira pushes worklogs to
                /webhooks/jira as they are saved, so synced entries are already
                in this list. Nothing to click, nothing to wait for. */}
            {/* Import sits beside Download. Gated on the `import_timesheets`
                grant, not the role: it logs time on behalf of other employees,
                so a custom role can hold it without being Admin/HR. */}
            {canImport && (
              <button
                onClick={() => setShowImport(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: T.rMd,
                  border: `1px solid ${T.border}`,
                  background: "var(--em-bg-card)",
                  color: T.fg2,
                  font: `600 12px ${T.font}`,
                  cursor: "pointer",
                  marginRight: 8,
                }}
              >
                <Upload size={14} /> Import
              </button>
            )}
            {canExport && (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowDownload((s) => !s)}
                disabled={!entries.length}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: T.rMd,
                  border: `1px solid ${T.border}`,
                  background: "var(--em-bg-card)",
                  color: T.fg2,
                  font: `600 12px ${T.font}`,
                  cursor: entries.length ? "pointer" : "not-allowed",
                  opacity: entries.length ? 1 : 0.5,
                }}
              >
                <Download size={14} /> Download ▾
              </button>
              {showDownload && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 4px)",
                    background: "var(--em-bg-card)",
                    border: `1px solid ${T.border}`,
                    borderRadius: T.rMd,
                    boxShadow: T.shadowMd,
                    zIndex: 20,
                    minWidth: 130,
                    overflow: "hidden",
                  }}
                >
                  {[
                    ["pdf", "PDF (.pdf)"],
                    ["csv", "CSV (.csv)"],
                    ["excel", "Excel (.xlsx)"],
                  ].map(([kind, label]) => (
                    <button
                      key={kind}
                      onClick={() => doExport(kind)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "9px 14px",
                        border: 0,
                        background: "none",
                        cursor: "pointer",
                        font: `500 12.5px ${T.font}`,
                        color: T.fg2,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
          {loading ? (
            <div style={{ padding: "14px 0", display: "flex", flexDirection: "column", gap: 12 }}>
              {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} h={38} radius={8} />)}
            </div>
          ) : entries.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: T.fg7,
                font: `400 13px ${T.font}`,
              }}
            >
              No time entries in this range.
            </div>
          ) : (
            pageDays.map(({ date, groups }) => {
              const dayMins = byDate[date].reduce(
                (s, e) => s + e.duration_minutes,
                0,
              );
              return (
                <div key={date}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 18px",
                      background: "#fafafa",
                      borderBottom: `1px solid ${T.divider}`,
                    }}
                  >
                    <span
                      style={{
                        font: `600 11px ${T.font}`,
                        color: T.fg5,
                        flex: 1,
                      }}
                    >
                      {fmtDate(date)}
                    </span>
                    <span
                      style={{
                        font: `700 11px ${T.font}`,
                        color: T.fg1,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtDuration(dayMins)}
                    </span>
                  </div>
                  {groups.map((g) =>
                    g.items.length > 1 ? renderGroup(date, g) : renderSingle(g.items[0]),
                  )}
                </div>
              );
            })
          )}
          {!loading && totalPages > 1 && (
            <div style={{ padding: "0 18px 14px" }}>
              <Pagination
                page={safePage}
                totalPages={totalPages}
                total={rows.length}
                pageSize={ENTRIES_PER_PAGE}
                onPage={goToPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right: summary + filters */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Panel title="This range">
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                font: `800 32px ${T.font}`,
                color: T.fg1,
                letterSpacing: "-0.02em",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtDuration(totalMins)}
            </span>
            <span style={{ font: `500 12px ${T.font}`, color: T.fg7 }}>
              logged
            </span>
          </div>
          <div
            style={{
              height: 8,
              borderRadius: 9999,
              background: T.bgMuted,
              overflow: "hidden",
              display: "flex",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: `${totalMins ? (billMins / totalMins) * 100 : 0}%`,
                background: T.success600,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              font: `500 11px ${T.font}`,
            }}
          >
            <span style={{ color: T.success600 }}>
              Billable {fmtDuration(billMins)}
            </span>
            <span style={{ color: T.fg7 }}>
              Internal {fmtDuration(totalMins - billMins)}
            </span>
          </div>
        </Panel>

        <Panel title="Filters" padding="14px 16px">
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {/* Quick ranges — they only write From/To, so any of the other
                filters stay applied on top of the preset. */}
            <div style={{ display: "flex", gap: 6 }}>
              {QUICK_RANGES.map((r) => {
                const on = activeQuick === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    title={r.title}
                    aria-pressed={on}
                    onClick={() => {
                      const [f, t] = r.range();
                      setFrom(f);
                      setTo(t);
                    }}
                    style={{
                      flex: 1,
                      padding: "5px 0",
                      borderRadius: 8,
                      border: `1px solid ${on ? T.orange : T.border}`,
                      background: on ? T.orange : "transparent",
                      color: on ? "#fff" : T.fg3,
                      font: `600 11px ${T.font}`,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FilterField label="From">
                  <Input
                    type="date"
                    style={FIELD_SM}
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </FilterField>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FilterField label="To">
                  <Input
                    type="date"
                    style={FIELD_SM}
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </FilterField>
              </div>
            </div>
            {canViewTeam && (
              <FilterField label="Employee">
                <EmployeeMultiSelect users={users} value={userIds} onChange={setUserIds} />
              </FilterField>
            )}
            <FilterField label="Project">
              <FilterSelect
                value={filterProject}
                onChange={setFilterProject}
                allLabel="All projects"
                buttonStyle={FILTER_SELECT_SM}
                options={projectOptionsOf(projects)}
              />
            </FilterField>
            {canViewTeam && (
              <FilterField label="Team">
                <Select
                  style={SELECT_SM}
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  <option value="">All teams</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
          </div>
        </Panel>
      </div>

      {showImport && (
        <ImportTimesheetModal
          onClose={() => setShowImport(false)}
          // Imported rows are normal Pending entries — the existing list and its
          // filters render them, so a plain re-fetch is all that's needed.
          onImported={() => load(true)}
        />
      )}

      {editEntry && (
        <EditEntryModal
          entry={editEntry}
          projects={projects}
          onClose={() => setEditEntry(null)}
          onSaved={(prevStatus) => {
            setEditEntry(null);
            toast.success(
              prevStatus === "approved" || prevStatus === "rejected"
                ? "Entry updated — sent for re-approval"
                : "Entry updated",
            );
            load();
          }}
        />
      )}

      {/* Non-working-day heads-up for the manual log card. Confirming sends the
          same body the click would have sent; cancelling leaves the form intact
          so the date can be corrected. */}
      <NonWorkingDayWarning
        warning={pendingLog?.warning}
        busy={saving}
        onConfirm={() => submitLog(pendingLog.body)}
        onCancel={() => setPendingLog(null)}
      />
    </div>
  );
}

// Who works on a task: its assignees and collaborators. Watchers only follow it.
const WORKING_ROLES = ["assignee", "collaborator"];

// Tasks of one project for a Task <select>; [] when no project is picked. A
// stale response (project switched mid-request) is dropped. With `userId`, only
// the tasks that person works on — a project member isn't on every task in it,
// and unassigned tasks are left out too.
function useProjectTasks(projectId, userId) {
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    setTasks([]);
    if (!projectId) return undefined;
    let cancelled = false;
    wsApi
      .projectTasks(projectId)
      .then((rows) => { if (!cancelled) setTasks(Array.isArray(rows) ? rows : []); })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [projectId]);
  return useMemo(() => {
    if (!userId) return tasks;
    return tasks.filter((t) =>
      (t.members || []).some(
        (m) => Number(m.user_id) === Number(userId) && WORKING_ROLES.includes(m.member_role),
      ));
  }, [tasks, userId]);
}

// ── Edit entry modal ─────────────────────────────────────────────────────────
function EditEntryModal({ entry, projects, onClose, onSaved }) {
  const hasRange = !!(entry.started_at && entry.ended_at);
  const [mode, setMode] = useState(hasRange ? "range" : "duration");
  const [projectId, setProjectId] = useState(entry.project_id ? String(entry.project_id) : "");
  const [taskId, setTaskId] = useState(entry.task_id ? String(entry.task_id) : "");
  // The entry owner's tasks — a manager editing someone's row sees theirs.
  const projectTasks = useProjectTasks(projectId, entry.user_id);
  // Same rule as projectOptions below: keep the entry's own task selectable so
  // the <select> never renders blank and saves task_id: null by accident.
  const taskOptions = useMemo(() => {
    if (
      !entry.task_id ||
      String(projectId) !== String(entry.project_id) ||
      projectTasks.some((t) => String(t.id) === String(entry.task_id))
    )
      return projectTasks;
    return [
      ...projectTasks,
      { id: entry.task_id, title: entry.task_title || `Task #${entry.task_id}` },
    ];
  }, [projectTasks, projectId, entry.task_id, entry.project_id, entry.task_title]);
  const [description, setDescription] = useState(entry.description || "");
  const [hours, setHours] = useState(String(Math.floor((entry.duration_minutes || 0) / 60)));
  const [minutes, setMinutes] = useState(String((entry.duration_minutes || 0) % 60));
  // Live: over 24 hours, negative, minutes > 59 (duration mode only).
  const editDurationError = mode === "duration" ? durationInputError(hours, minutes) : null;
  const [entryDate, setEntryDate] = useState(String(entry.entry_date || "").slice(0, 10));

  // An entry logged by DURATION has no start/end. Opening "Start & end" on it
  // used to default both to now on TODAY's date. Instead both start on the
  // entry's own date at the current time of day — the same value, no
  // calculation: the user sets the actual times. An entry logged by
  // Start & end shows the times that were entered.
  const seedRange = (dateStr) => {
    const now = new Date();
    const [y, m, d] = String(dateStr || "").split("-").map(Number);
    const at = y && m && d
      ? new Date(y, m - 1, d, now.getHours(), now.getMinutes())
      : now;
    const v = toLocalInputValue(at);
    return [v, v];
  };
  const initialRange = hasRange
    ? [toLocalInputValue(entry.started_at), toLocalInputValue(entry.ended_at)]
    : seedRange(entry.entry_date);
  const [startAt, setStartAt] = useState(initialRange[0]);
  const [endAt, setEndAt] = useState(initialRange[1]);
  const rangeTouched = useRef(false); // user edited Start/End by hand
  const [billable, setBillable] = useState(!!entry.billable);
  const [saving, setSaving] = useState(false);
  // Same non-working-day warning as the log card — moving an entry ONTO a
  // weekend or holiday deserves the same heads-up as creating one there.
  const { checkDay } = useNonWorkingDays();
  const [pendingSave, setPendingSave] = useState(null);

  const rangeMins = (() => {
    if (mode !== "range" || !startAt || !endAt) return null;
    const ms = new Date(endAt).getTime() - new Date(startAt).getTime();
    return ms > 0 ? Math.round(ms / 60000) : null;
  })();

  const editEntryDate =
    mode === "range" ? (startAt ? localDateStr(new Date(startAt)) : "") : entryDate;
  const editDayWarning = checkDay(editEntryDate);

  // The project list is scoped to what the viewer is a member of, but an entry
  // can sit on a project they're NOT on (logged before they were removed, or
  // synced from Jira). A <select> whose value matches no <option> renders blank
  // and would save project_id: null — silently detaching the entry from its
  // project. So always keep the entry's own project in the options.
  const projectOptions = useMemo(() => {
    if (
      !entry.project_id ||
      projects.some((p) => String(p.id) === String(entry.project_id))
    )
      return projects;
    return [
      ...projects,
      {
        id: entry.project_id,
        name: entry.project_name || `Project #${entry.project_id}`,
      },
    ];
  }, [projects, entry.project_id, entry.project_name]);

  // Validation only — returns the request body, or null after toasting why not.
  const buildSaveBody = () => {
    if (!projectId) {
      toast.error("Select a project");
      return null;
    }
    if (!description.trim()) {
      toast.error("Enter a description");
      return null;
    }
    const body = {
      project_id: projectId || null,
      task_id: taskId || null,
      description,
      billable: billable ? 1 : 0,
    };
    if (mode === "range") {
      if (rangeMins == null) {
        toast.error("End time must be after start time");
        return null;
      }
      if (rangeMins > 1440) {
        toast.error("A single entry can't exceed 24 hours");
        return null;
      }
      // Send true instants (ISO) + the local calendar day, so the server never
      // has to guess a zone for the wall-clock times the user typed.
      return {
        ...body,
        started_at: localInputToIso(startAt),
        ended_at: localInputToIso(endAt),
        entry_date: localDateStr(new Date(startAt)),
      };
    }
    if (editDurationError) {
      toast.error(editDurationError);
      return null;
    }
    const mins = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    if (mins <= 0) {
      toast.error("Enter a duration");
      return null;
    }
    return { ...body, duration_minutes: mins, entry_date: entryDate };
  };

  const submitSave = async (body) => {
    setSaving(true);
    try {
      const res = await wsApi.updateTimeEntry(entry.id, body);
      setPendingSave(null);
      onSaved(res?.prevStatus);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not update entry");
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const body = buildSaveBody();
    if (!body) return;
    const warning = checkDay(body.entry_date);
    if (warning) return setPendingSave({ body, warning });
    submitSave(body);
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 460, background: "var(--em-bg-card)", borderRadius: 14, boxShadow: T.shadowMd, padding: 20 }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>Edit time entry</span>
          <button onClick={onClose} style={{ background: "none", border: 0, cursor: "pointer", color: T.fg5, display: "flex" }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <Label>Project</Label>
            <FilterSelect
              value={projectId}
              onChange={(v) => {
                setProjectId(v);
                setTaskId("");
              }}
              allLabel="Select a project"
              buttonStyle={FILTER_SELECT_FIELD}
              options={projectOptionsOf(projectOptions)}
            />
          </div>
          <div>
            <Label>Task</Label>
            <Select value={taskId} disabled={!projectId} onChange={(e) => setTaskId(e.target.value)}>
              <option value="">{projectId ? "No task" : "Select a project first"}</option>
              {taskOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What did you work on?" required />
          </div>

          {/* Duration | Start & end toggle */}
          <div style={{ display: "flex", background: T.bgMuted, borderRadius: 8, padding: 2, width: "fit-content" }}>
            {[["duration", "Duration"], ["range", "Start & end"]].map(([m, label]) => (
              <button
                key={m}
                onClick={() => {
                  // Duration → Start & end on a duration entry: re-seed the
                  // range on the (possibly changed) date field, unless the
                  // user already set the range by hand.
                  if (m === "range" && mode !== "range" && !hasRange && !rangeTouched.current) {
                    const [s, e] = seedRange(entryDate);
                    setStartAt(s);
                    setEndAt(e);
                  }
                  setMode(m);
                }}
                style={{ border: 0, borderRadius: 6, padding: "5px 12px", cursor: "pointer", font: `600 12px ${T.font}`, background: mode === m ? T.orange : "transparent", color: mode === m ? "#fff" : T.fg5 }}
              >{label}</button>
            ))}
          </div>

          {mode === "duration" ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ width: 80 }}>
                <Label>Hrs</Label>
                <Input type="number" value={hours} onChange={(e) => setHours(wrapSpinValue(hours, e.target.value, MAX_ENTRY_HOURS))}
                  aria-invalid={!!editDurationError} style={editDurationError ? invalidInput : undefined} />
              </div>
              <div style={{ width: 80 }}>
                <Label>Min</Label>
                <Input type="number" value={minutes} onChange={(e) => setMinutes(wrapSpinValue(minutes, e.target.value, 59))}
                  aria-invalid={!!editDurationError} style={editDurationError ? invalidInput : undefined} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Label>Date</Label>
                <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
              </div>
              {editDurationError && (
                <div role="alert" style={{ flexBasis: "100%", font: `500 11.5px ${T.font}`, color: T.danger }}>
                  {editDurationError}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Label>Start</Label>
                  <Input type="datetime-local" value={startAt} onChange={(e) => { rangeTouched.current = true; setStartAt(e.target.value); }} />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Label>End</Label>
                  <Input type="datetime-local" value={endAt} onChange={(e) => { rangeTouched.current = true; setEndAt(e.target.value); }} />
                </div>
              </div>
              <div style={{ font: `500 11.5px ${T.font}`, color: rangeMins == null ? T.danger : T.fg7 }}>
                {rangeMins == null ? "End time must be after start time" : `Duration: ${fmtDuration(rangeMins)}`}
              </div>
            </div>
          )}

          {editDayWarning && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, font: `500 11.5px ${T.font}`, color: T.warn }}>
              <CalendarOff size={13} />
              Selected date is {nonWorkingLabel(editDayWarning)}.
            </div>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 7, font: `500 13px ${T.font}`, color: T.fg3, cursor: "pointer" }}>
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} style={{ accentColor: T.orange }} /> Billable
          </label>

          {(entry.status === "approved" || entry.status === "rejected") && (
            <div style={{ font: `500 11.5px ${T.font}`, color: "#c2410c", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "8px 10px" }}>
              This entry is <strong>{entry.status}</strong>. Saving changes will send it back for re-approval.
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "8px 14px", borderRadius: T.rMd, border: `1px solid ${T.border}`, background: "var(--em-bg-card)", color: T.fg2, font: `600 13px ${T.font}`, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            style={{ padding: "8px 16px", borderRadius: T.rMd, border: 0, background: T.gradient, color: "#fff", font: `600 13px ${T.font}`, cursor: "pointer", boxShadow: T.shadowBrand }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Renders above this modal (z-index 1100 vs 1000). */}
      <NonWorkingDayWarning
        warning={pendingSave?.warning}
        busy={saving}
        confirmLabel="Save anyway"
        onConfirm={() => submitSave(pendingSave.body)}
        onCancel={() => setPendingSave(null)}
      />
    </div>
  );
}
